import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import { FaCircle, FaPaperPlane, FaRegCommentDots, FaDownload, FaShareAlt } from 'react-icons/fa'
import { API_BASE, apiFetch } from '../lib/api'
import MarkdownMessage from '../components/MarkdownMessage'
import RecentChats, { ChatThread } from '../components/RecentChats'
import { useAuth } from '../context/AuthContext'

type ChatMessage = {
    id: string | number
    sender: 'user' | 'assistant' | 'llm' | 'system'
    userName?: string | null
    text: string
    createdAt: string
}

type Agent = string

const WS_BASE = API_BASE.replace(/^http/, 'ws')

const makeMessage = (sender: ChatMessage['sender'], text: string): ChatMessage => ({
    id: crypto.randomUUID(),
    sender,
    text,
    createdAt: new Date().toISOString(),
})

const formatTimestamp = (value: string) => {
    const m = moment(value)
    return m.isValid() ? m.format('lll') : ''
}

export default function Demo() {
    const { user } = useAuth()
    const ws = useRef<WebSocket | null>(null)
    const reconnectTimerRef = useRef<number | null>(null)
    const shouldReconnectRef = useRef(true)
    const messageListRef = useRef<HTMLDivElement | null>(null)

    const [agents, setAgents] = useState<Agent[]>([])
    const [agentIcons, setAgentIcons] = useState<Record<string, string>>({})
    const [suggestedQuestions, setSuggestedQuestions] = useState<Record<string, string[]>>({})

    const [selectedAgent, setSelectedAgent] = useState<Agent>(() => {
        const saved = localStorage.getItem('filtron_selectedAgent')
        return saved ? saved : 'Police Station Officer'
    })
    const selectedAgentRef = useRef(selectedAgent)
    const [agentDropdownOpen, setAgentDropdownOpen] = useState(false)
    const agentDropdownRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const res = await apiFetch(`${API_BASE}/api/constants/personas`)
                if (res.ok) {
                    const data = await res.json()
                    setAgents(data.personas || [])
                    setAgentIcons(data.agentIcons || {})
                    setSuggestedQuestions(data.suggestedQuestions || {})
                    if (data.personas?.length > 0 && !data.personas.includes(selectedAgent)) {
                        setSelectedAgent(data.personas[0])
                    }
                    // If local storage has a value but it's invalid, the above will reset it
                }
            } catch (err) {
                console.error('Failed to fetch agents', err)
            }
        }
        fetchAgents()
    }, [])

    // Persist selectedAgent to localStorage
    useEffect(() => {
        localStorage.setItem('filtron_selectedAgent', selectedAgent)
    }, [selectedAgent])

    useEffect(() => {
        selectedAgentRef.current = selectedAgent
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
                type: 'agent_changed',
                payload: { agent: selectedAgent }
            }))
        }
    }, [selectedAgent])

    const [isWsConnected, setIsWsConnected] = useState(false)
    const [llmRunning, setLlmRunning] = useState(false)
    const [input, setInput] = useState('')

    const [threads, setThreads] = useState<ChatThread[]>([])
    const [activeThreadId, setActiveThreadId] = useState<number | null>(() => {
        const saved = localStorage.getItem('filtron_activeThreadId')
        return saved ? Number(saved) : null
    })

    // Persist activeThreadId to localStorage
    useEffect(() => {
        if (activeThreadId === null) {
            localStorage.removeItem('filtron_activeThreadId')
        } else {
            localStorage.setItem('filtron_activeThreadId', String(activeThreadId))
        }
    }, [activeThreadId])
    const [showArchived, setShowArchived] = useState(false)

    const [messages, setMessages] = useState<ChatMessage[]>([
        makeMessage(
            'system',
            'Your AI Assistant is ready. Ask for FIR lookup, 112 call review, complaint status, linked records, station summaries, or SLA breaches.'
        ),
    ])

    const canSend = useMemo(() => {
        return !llmRunning && input.trim().length > 0
    }, [input, llmRunning])

    useEffect(() => {
        const el = messageListRef.current
        if (!el) return
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight
        })
    }, [messages, llmRunning])

    useEffect(() => {
        if (!agentDropdownOpen) return
        const handleClick = (e: MouseEvent) => {
            if (agentDropdownRef.current?.contains(e.target as Node)) return
            setAgentDropdownOpen(false)
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [agentDropdownOpen])

    // Load Threads
    const fetchThreads = async () => {
        try {
            const res = await apiFetch(`${API_BASE}/api/chat/threads?archived=${showArchived}`)
            if (res.ok) {
                const data = await res.json()
                setThreads(data.threads || [])
            }
        } catch (err) {
            console.error('Failed to fetch threads', err)
        }
    }

    useEffect(() => {
        fetchThreads()
    }, [showArchived])

    // Load Messages when activeThreadId changes
    useEffect(() => {
        if (!activeThreadId) {
            setMessages([
                makeMessage(
                    'system',
                    'Your AI Assistant is ready. Ask for FIR lookup, 112 call review, complaint status, linked records, station summaries, or SLA breaches.'
                ),
            ])
            return
        }

        const fetchMessages = async () => {
            try {
                const res = await apiFetch(`${API_BASE}/api/chat/threads/${activeThreadId}/messages`)
                if (res.ok) {
                    const data = await res.json()
                    const loadedMessages = (data.messages || []).map((m: any) => ({
                        id: m.id,
                        sender: m.sender,
                        userName: m.user_name ?? null,
                        text: m.text,
                        createdAt: m.created_at,
                    }))

                    if (loadedMessages.length === 0) {
                        setMessages([
                            makeMessage(
                                'system',
                                'This is a new conversation. Ask a police data question to start.'
                            ),
                        ])
                    } else {
                        setMessages(loadedMessages)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch messages', err)
            }
        }

        fetchMessages()
    }, [activeThreadId])

    useEffect(() => {
        shouldReconnectRef.current = true

        const createWS = () => {
            if (!shouldReconnectRef.current) return

            const params = new URLSearchParams()
            params.set('agent', selectedAgentRef.current || (agents.length > 0 ? agents[0] : 'Police Station Officer'))
            const socket = new WebSocket(`${WS_BASE}/api/ws?${params.toString()}`)
            ws.current = socket

            const clearTimer = () => {
                if (reconnectTimerRef.current) {
                    clearTimeout(reconnectTimerRef.current)
                    reconnectTimerRef.current = null
                }
            }

            socket.onopen = () => {
                setIsWsConnected(true)
                clearTimer()
            }

            socket.onmessage = (event) => {
                if (typeof event.data !== 'string') return

                try {
                    const eventData = JSON.parse(event.data)

                    if (eventData.type === 'alert' && eventData.message) {
                        toast.info(String(eventData.message))
                    } else if (eventData.type === 'new_message' && eventData.payload) {
                        const { message, thread } = eventData.payload

                        // Update threads list
                        setThreads((prevThreads) => {
                            const index = prevThreads.findIndex(t => t.id === thread.id)
                            if (index >= 0) {
                                const newThreads = [...prevThreads]
                                newThreads[index] = thread
                                // Sort so updated threads go to top
                                return newThreads.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
                            } else {
                                // New thread
                                return [thread, ...prevThreads]
                            }
                        })

                        // Update messages if this is the active thread
                        setActiveThreadId((currentActiveThreadId) => {
                            if (currentActiveThreadId === thread.id) {
                                setMessages((prevMessages) => {
                                    // Remove optimistic message if this is the real one
                                    const filtered = prevMessages.filter(m => m.id !== 'optimistic_user_msg')
                                    // Check if message already exists (prevent duplicates if needed, but uuid differs)
                                    if (filtered.some(m => m.id === message.id)) return filtered

                                    return [...filtered, {
                                        id: message.id,
                                        sender: message.sender,
                                        userName: message.user_name ?? null,
                                        text: message.text,
                                        createdAt: message.created_at,
                                    }]
                                })
                                // If assistant message arrived, clear llmRunning
                                if (message.sender === 'assistant') {
                                    setLlmRunning(false)
                                }
                            }
                            return currentActiveThreadId
                        })
                    }
                } catch (err) {
                    console.warn('Failed to parse WS message', err)
                }
            }

            socket.onclose = () => {
                setIsWsConnected(false)
                clearTimer()
                if (shouldReconnectRef.current) {
                    reconnectTimerRef.current = window.setTimeout(createWS, 5000)
                }
            }

            socket.onerror = () => {
                setIsWsConnected(false)
            }
        }

        createWS()

        return () => {
            shouldReconnectRef.current = false
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current)
                reconnectTimerRef.current = null
            }
            ws.current?.close()
        }
    }, [])

    const handleNewChat = () => {
        setActiveThreadId(null)
    }

    const handleArchiveThread = async (threadId: number, archive: boolean) => {
        try {
            const endpoint = archive ? 'archive' : 'unarchive'
            const res = await apiFetch(`${API_BASE}/api/chat/threads/${threadId}/${endpoint}`, {
                method: 'PATCH',
            })
            if (res.ok) {
                if (activeThreadId === threadId && archive && !showArchived) {
                    setActiveThreadId(null)
                }
                fetchThreads()
            } else {
                toast.error('Failed to update thread')
            }
        } catch (err) {
            console.error('Archive failed', err)
        }
    }

    const sendQuestion = async (question = input) => {
        const text = question.trim()
        if (!text) return

        setInput('')
        setLlmRunning(true)

        try {
            let threadId = activeThreadId

            // Create thread if none active
            if (!threadId) {
                const res = await apiFetch(`${API_BASE}/api/chat/threads`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title: text, initiator: 'webchat' })
                })
                if (res.ok) {
                    const data = await res.json()
                    threadId = data.thread.id
                    setActiveThreadId(threadId)
                    // Optimistic add to list
                    setThreads(prev => [data.thread, ...prev])
                } else {
                    throw new Error('Failed to create thread')
                }
            }
            // Immediately show user message optimistically
            const optimisticUserMsg = { ...makeMessage('user', text), id: 'optimistic_user_msg', userName: null }
            setMessages((prev) => [...prev.filter(m => m.sender !== 'system'), optimisticUserMsg])

            // Send message via WebSocket
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                ws.current.send(JSON.stringify({
                    type: 'send_message',
                    payload: { threadId, text }
                }))
            } else {
                toast.error('WebSocket is not connected. Please try again.')
                setLlmRunning(false)
            }
        } catch (err) {
            console.error('Failed to send question', err)
            toast.error('Failed to send message')
            setLlmRunning(false)
        }
    }

    const clearConversation = () => {
        handleNewChat()
    }

    const handleDownloadCSV = () => {
        const exportable = messages.filter(m => m.sender === 'user' || m.sender === 'assistant')
        if (exportable.length === 0) return

        const escape = (val: string) => `"${val.replace(/"/g, '""')}"`
        const header = ['Timestamp', 'Sender', 'Name', 'Message']
        const rows = exportable.map(m => [
            escape(new Date(m.createdAt).toLocaleString()),
            escape(m.sender),
            escape(m.sender === 'user' ? (m.userName ?? 'User') : 'Filtron AI'),
            escape(m.text),
        ])
        const csv = [header, ...rows].map(r => r.join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const title = threads.find(t => t.id === activeThreadId)?.title ?? 'chat'
        a.download = `${title.replace(/[^a-z0-9]+/gi, '_')}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    const handleShare = async () => {
        if (!activeThreadId) return

        try {
            const res = await apiFetch(`${API_BASE}/api/chat/threads/${activeThreadId}/share`, {
                method: 'POST',
            })
            
            if (!res.ok) {
                toast.error('Failed to generate share link')
                return
            }

            const data = await res.json()
            const shareUrl = `${window.location.origin}/shared/${data.token}`

            if (navigator.share) {
                try {
                    await navigator.share({ title: 'Shared Chat', url: shareUrl })
                } catch {
                    // user cancelled or share failed silently
                    await navigator.clipboard.writeText(shareUrl)
                    toast.success('Share link copied to clipboard')
                }
            } else {
                await navigator.clipboard.writeText(shareUrl)
                toast.success('Share link copied to clipboard')
            }
        } catch (err) {
            console.error('Share failed', err)
            toast.error('Failed to generate share link')
        }
    }

    return (
        <div className="h-full bg-slate-100 flex flex-col">
            <main className="flex-1 flex gap-4 p-4 min-h-0 overflow-hidden">
                {/* Left Sidebar: Recent Chats */}
                <RecentChats
                    threads={threads}
                    activeThreadId={activeThreadId}
                    onSelectThread={setActiveThreadId}
                    onNewChat={handleNewChat}
                    onArchiveThread={handleArchiveThread}
                    showArchived={showArchived}
                    onToggleShowArchived={() => setShowArchived(!showArchived)}
                />

                {/* Center: Chat Area */}
                <section className="flex flex-col flex-1 min-w-0 min-h-0 bg-white rounded-lg shadow-md border border-slate-200">
                    <header className="border-b border-slate-200 p-4 shrink-0 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <h1 className="text-xl font-semibold text-slate-900">Police AI Assistant</h1>
                            {activeThreadId && (
                                <p className="mt-0.5 text-sm text-slate-500 truncate">
                                    <span className="font-mono text-slate-400">#{activeThreadId}</span>
                                    {' '}
                                    {threads.find(t => t.id === activeThreadId)?.title ?? ''}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={handleDownloadCSV}
                                data-tooltip-id="my-tooltip"
                                data-tooltip-content="Download as CSV"
                                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
                            >
                                <FaDownload size={15} />
                            </button>
                            <button
                                onClick={handleShare}
                                data-tooltip-id="my-tooltip"
                                data-tooltip-content="Share chat (valid for 24 hours)"
                                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
                            >
                                <FaShareAlt size={15} />
                            </button>
                        </div>
                    </header>

                    <div ref={messageListRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                        {messages.map((message) => {
                            if (message.sender === 'system') {
                                return (
                                    <div key={message.id} className="flex justify-center">
                                        <div className="max-w-2xl rounded-md bg-white border border-slate-200 px-4 py-2 text-center text-sm text-slate-500 shadow-sm">
                                            {message.text}
                                        </div>
                                    </div>
                                )
                            }

                            const isUser = message.sender === 'user'
                            const senderName = isUser
                                ? (message.userName ?? 'User')
                                : 'Filtron AI'
                            return (
                                <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                    <div style={{ maxWidth: isUser ? '70%' : '90%' }} className={`rounded-lg px-4 py-3 shadow-sm ${isUser ? 'bg-slate-700 text-white' : 'border border-slate-200 bg-white text-slate-900'}`}>
                                        <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
                                            <FaRegCommentDots className={isUser ? 'text-slate-200' : 'text-blue-600'} />
                                            {senderName}
                                        </div>
                                        {isUser ? (
                                            <p className="whitespace-pre-wrap">{message.text}</p>
                                        ) : (
                                            <MarkdownMessage text={message.text} className="text-slate-900" />
                                        )}
                                        <div className={`mt-2 text-xs ${isUser ? 'text-slate-300' : 'text-slate-500'}`}>
                                            {formatTimestamp(message.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}

                        {llmRunning && (
                            <div className="flex justify-start">
                                <div className="rounded-md bg-white border border-slate-200 shadow-sm px-4 py-2 text-sm text-slate-500">
                                    Filtron AI is thinking...
                                </div>
                            </div>
                        )}
                    </div>

                    <form
                        className="flex gap-2 border-t border-slate-200 p-4 shrink-0 bg-white"
                        onSubmit={(event) => {
                            event.preventDefault()
                            sendQuestion()
                        }}
                    >
                        <input
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            placeholder="Ask about FIRs, 112 calls, complaints, stations, SLA breaches, or linked records"
                            className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            disabled={llmRunning}
                        />
                        <button
                            type="submit"
                            disabled={!canSend}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 shrink-0"
                            data-tooltip-id="my-tooltip"
                            data-tooltip-content="Ask assistant"
                        >
                            <FaPaperPlane className="text-sm" />
                        </button>
                    </form>
                </section>

                {/* Right Sidebar */}
                <aside className="hidden lg:flex flex-col w-80 bg-white border border-slate-200 rounded-lg shadow-md shrink-0 min-h-0">
                    {/* Top bar: connection status */}
                    <div className="p-3 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-lg shrink-0">
                        <div className="flex items-center overflow-hidden mr-2">
                            <span className="text-sm font-semibold text-slate-700 truncate" title={user?.name || 'Guest'}>
                                {user?.name || 'Guest'}
                            </span>
                        </div>
                        <span className={`flex items-center shrink-0 gap-2 rounded-full px-2.5 py-1 text-xs font-medium ${isWsConnected ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                            <FaCircle className="text-[0.55rem]" />
                            {isWsConnected ? 'Connected' : 'Reconnecting'}
                        </span>
                    </div>

                    {/* Agent selector */}
                    <div className="p-4 border-b border-slate-200 shrink-0">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Agent</p>
                        <div ref={agentDropdownRef} className="relative">
                            <button
                                type="button"
                                onClick={() => setAgentDropdownOpen((o) => !o)}
                                className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition-colors hover:border-blue-300 hover:bg-blue-50"
                            >
                                <span className="text-lg leading-none">{agentIcons[selectedAgent] || '🏢'}</span>
                                <span className="flex-1 min-w-0">
                                    <span className="block truncate text-sm font-medium text-slate-800">{selectedAgent}</span>
                                </span>
                                <svg className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${agentDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {agentDropdownOpen && (
                                <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-slate-200 bg-white py-1 shadow-xl">
                                    {agents.map((agent) => {
                                        const active = agent === selectedAgent
                                        return (
                                            <button
                                                key={agent}
                                                type="button"
                                                onClick={() => { setSelectedAgent(agent); setAgentDropdownOpen(false) }}
                                                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${active ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
                                            >
                                                <span className="text-base leading-none">{agentIcons[agent] || '🏢'}</span>
                                                <span className={`flex-1 min-w-0 truncate text-sm ${active ? 'font-semibold' : 'font-medium'}`}>{agent}</span>
                                                {active && (
                                                    <svg className="h-4 w-4 shrink-0 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Suggested queries */}
                    <div className="border-b border-slate-200 px-4 pt-4 pb-3 shrink-0">
                        <h2 className="text-sm font-semibold text-slate-900">Suggested Queries</h2>
                        <p className="mt-0.5 text-xs text-slate-500">Tap to send directly to the assistant.</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
                        {(suggestedQuestions[selectedAgent] || []).map((question) => (
                            <button
                                key={question}
                                onClick={() => sendQuestion(question)}
                                disabled={llmRunning}
                                className="w-full cursor-pointer text-left p-3 rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {question}
                            </button>
                        ))}
                    </div>
                </aside>
            </main>
        </div>
    )
}
